import { useState } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { typeInfo } from "../../../../../constants/typeInfo";
import { applyUnderline, renderWithBlanks } from "../../../../../utils/textUtils";

import CommonPage from "../../common/CommonPage";

const BasicChoicePage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();

  const [overlayVisible, setOverlayVisible] = useState(false);

  // [모의고사 리디자인] 5지선다 본진 — 제목·주제·빈칸·순서·어휘·요약문AB 등
  // 단일정답 객관식 전 계열이 이 파일로 라우팅된다(ParagraphPage includes 목록).
  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768;

  const selectOption = ({ optionIndex }) => {
    const realIndex = optionIndex + 1;
    const isCorrect = realIndex === currentPage.answer;
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          isCorrect,
          input: realIndex,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setOverlayVisible(true);
    setTimeout(() => {
      setOverlayVisible(false);
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      currentPage={currentPage}
      handlePreviousPage={handlePreviousPage}
      handleNextPage={handleNextPage}
      totalNo={pageList.length}
      paragraph={currentPage.text}
      accentColor={typeInfo.paragraph.color}
      overlayVisible={overlayVisible}
      isCorrect={currentPage.isCorrect}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // [여백 수술] 폰은 화면 기준 폭 — 조상 요소가 좁혀도 면역
          width: isMobileW ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "100%",
          maxWidth: "680px", // [모의고사 리디자인] 75vw 전폭 바 → 중앙 열
          margin: "0 auto",
          gap: isMobileW ? "14px" : "20px",
        }}
      >
        {/* Q칩 + 카운터 + 진행바 — 어법과 동일 문법, 모의고사 토큰 색 */}
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: isMobileW ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
              Q{currentIndex + 1}
            </span>
            <span style={{ marginLeft: "auto", fontSize: isMobileW ? "12px" : "13px", color: "#7A8490", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.paragraph.color, borderRadius: "999px", transition: "width .25s" }} />
          </div>
        </div>
        <div
          style={{
            fontSize: isMobileW ? "16px" : "19px", // [폰트 통일] 질문 반 단계 다운
            lineHeight: 1.75,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            wordBreak: "keep-all",
          }}
        >
          {/* 번호는 Q칩으로 분리 · 질문 속 마커는 renderWithBlanks가 처리 */}
          {renderWithBlanks(
            currentPage.question.replace(
              "적고, 틀린 표현들을 모두 올바른 표현으로 고치시오.",
              "고르세요."
            )
          )}
        </div>
        {currentPage.type === "summary_blank_AB" && currentPage.text2 && (
          <div
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E6EDEC", // 카드 스킨
              borderRadius: "12px",
              padding: isMobileW ? "13px 14px" : "16px 20px",
              fontSize: isMobileW ? "15px" : "18px",
              lineHeight: "1.9",
              whiteSpace: "pre-wrap",
              wordBreak: "keep-all",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: isMobileW ? "12px" : "13px",
                letterSpacing: "0.06em",
                color: typeInfo.paragraph.color,
                marginBottom: "6px",
              }}
            >
              요약문
            </div>
            {/* [밤티 수술 본진] text2 날것 출력이 --**(A)**-- 별표 노출의 원인 —
                renderWithBlanks가 (A)/(B)를 밑줄 빈칸 칩으로 렌더 */}
            {renderWithBlanks(currentPage.text2)}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {currentPage.choices.map((option, optionIndex) => {
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  boxSizing: "border-box",
                  padding: isMobileW ? "12px 13px" : "14px 18px",
                  border: currentPage.isSubmitted
                    ? currentPage.answer === optionIndex + 1
                      ? "1.5px solid #00D9C0"
                      : currentPage.input === optionIndex + 1
                      ? "1.5px solid #C2452D"
                      : "1px solid #E6EDEC"
                    : "1px solid #E6EDEC",
                  borderRadius: "12px",
                  fontWeight:
                    currentPage.isSubmitted &&
                    (optionIndex + 1 === currentPage.answer ||
                      optionIndex + 1 === currentPage.input)
                      ? "bold"
                      : "normal",
                  cursor: !currentPage.isSubmitted && "pointer",
                  // [모의고사 리디자인] 조건 분기 유지, 색만 ECHO 팔레트:
                  // 정답 teal-soft / 잘못 고른 보기 red-soft (기존 반투명 빨강+흰글자 제거)
                  backgroundColor:
                    currentPage.isSubmitted &&
                    currentPage.answer === optionIndex + 1
                      ? "#E5FBF8"
                      : currentPage.input === optionIndex + 1 && "#FCEDE9",
                  color:
                    currentPage.isSubmitted &&
                    currentPage.answer === optionIndex + 1
                      ? "#00A396"
                      : currentPage.input === optionIndex + 1 && "#C2452D",
                  fontSize: isMobileW ? "15px" : "16.5px", // [폰트 통일]
                  fontWeight: 500, // [폰트 통일] 기본 400 → 중간 굵기, 질문과 한 세트
                  letterSpacing: "-0.01em",
                  lineHeight: 1.65,
                  wordBreak: "keep-all",
                }}
                onClick={() => {
                  if (currentPage.isSubmitted) return;
                  selectOption({ optionIndex });
                }}
              >
                {option}
              </div>
            );
          })}
        </div>
      </div>
    </CommonPage>
  );
};

export default BasicChoicePage;
